import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getServiceOrders, getServiceOrdersKanban, updateServiceOrder, deleteServiceOrder } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, LayoutGrid, List, Eye, Edit, Trash2, MoreHorizontal, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getOrderStatusClass } from '@/lib/utils';
import SmartSearch from '@/components/SmartSearch';

const statuses = ['Draft', 'Pending', 'Documents Collected', 'Submitted', 'In Progress', 'Completed', 'Delivered', 'Cancelled', 'On Hold'];
const priorities = ['Normal', 'Urgent', 'VIP'];

// Column definitions for smart search
const searchColumns = [
  { key: 'order_number', label: 'Order #', type: 'text' },
  { key: 'client_name', label: 'Client', type: 'text' },
  { key: 'services_summary', label: 'Services', type: 'text' },
  { key: 'order_workers_display', label: 'Workers', type: 'text' },
  { key: 'beneficiary_name', label: 'Beneficiary', type: 'text' },
  { key: 'grand_total', label: 'Amount', type: 'number' },
  { key: 'priority', label: 'Priority', type: 'select', options: priorities.map(p => ({ value: p, label: p })) },
  { key: 'status', label: 'Status', type: 'select', options: statuses.map(s => ({ value: s, label: s })) },
  { key: 'created_at', label: 'Date', type: 'date' },
  { key: 'reference_number', label: 'Reference', type: 'text' },
];

// Quick filter presets
const quickFilters = [
  { label: 'Pending', filters: { status: 'Pending' } },
  { label: 'In Progress', filters: { status: 'In Progress' } },
  { label: 'Completed', filters: { status: 'Completed' } },
  { label: 'Urgent', filters: { priority: 'Urgent' } },
  { label: 'VIP', filters: { priority: 'VIP' } },
  { label: 'Today', filters: { created_at: 'today' } },
];

export default function ServiceOrdersPage() {
  const navigate = useNavigate();
  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [kanbanData, setKanbanData] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  const loadOrders = useCallback(async () => {
    try {
      const res = await getServiceOrders({ limit: 500 });
      setAllOrders(res.data.items);
      setFilteredOrders(res.data.items);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadKanban = useCallback(async () => {
    try {
      const res = await getServiceOrdersKanban();
      setKanbanData(res.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (viewMode === 'kanban') {
      loadKanban();
    }
  }, [viewMode, loadKanban]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateServiceOrder(orderId, { status: newStatus });
      toast.success('Status updated');
      loadOrders();
      if (viewMode === 'kanban') loadKanban();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    try {
      await deleteServiceOrder(orderToDelete.id);
      toast.success(`Order ${orderToDelete.order_number} deleted successfully`);
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      loadOrders();
      if (viewMode === 'kanban') loadKanban();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const handleDuplicate = (order) => {
    navigate(`/service-orders/new?duplicate=${order.id}`);
  };

  const kanbanColumns = ['Draft', 'Pending', 'In Progress', 'Completed', 'Delivered'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Service Orders</h1>
          <p className="text-muted-foreground">Track and manage service requests</p>
        </div>
        <Button onClick={() => navigate('/service-orders/new')} data-testid="new-order-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Order
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="space-y-4">
            <SmartSearch
              data={allOrders}
              columns={searchColumns}
              onFilter={setFilteredOrders}
              quickFilters={quickFilters}
              placeholder="Search by order #, client, service, worker, amount..."
            />
            <div className="flex justify-end">
              <div className="flex gap-1 border rounded-lg p-1">
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('kanban')}
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'list' ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Services</TableHead>
                    <TableHead>Workers</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                      <TableCell className="font-mono">
                        <Link to={`/service-orders/${order.id}`} className="hover:text-primary">
                          {order.order_number}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(order.created_at)}</TableCell>
                      <TableCell className="font-medium">{order.client_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[180px]" title={order.services_summary || order.service_name}>
                            {order.services_summary || order.service_name}
                          </span>
                          {order.item_count > 1 && (
                            <Badge variant="secondary" className="text-xs">
                              {order.item_count} items
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {order.order_workers_display || order.assigned_workers_display || order.beneficiary_name || '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(order.grand_total)}</TableCell>
                      <TableCell>
                        <Badge variant={order.priority === 'Urgent' ? 'destructive' : order.priority === 'VIP' ? 'default' : 'secondary'}>
                          {order.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={order.status}
                          onValueChange={(v) => handleStatusChange(order.id, v)}
                        >
                          <SelectTrigger className="h-7 w-[130px]">
                            <Badge className={getOrderStatusClass(order.status)}>{order.status}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`order-actions-${order.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/service-orders/${order.id}`)} data-testid={`view-order-${order.id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/service-orders/${order.id}`)} data-testid={`edit-order-${order.id}`}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Order
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(order)} data-testid={`duplicate-order-${order.id}`}>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDeleteClick(order)} 
                              className="text-red-600 focus:text-red-600"
                              data-testid={`delete-order-${order.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Order
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredOrders.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No orders found matching your search criteria
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {kanbanColumns.map((column) => (
                <div key={column} className="min-w-[280px] flex-shrink-0">
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-sm">{column}</h3>
                      <Badge variant="secondary">{(kanbanData[column] || []).length}</Badge>
                    </div>
                    <div className="space-y-2">
                      {(kanbanData[column] || []).map((order) => (
                        <Card key={order.id} className="cursor-pointer hover:bg-accent/50" onClick={() => navigate(`/service-orders/${order.id}`)}>
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-mono text-xs">{order.order_number}</span>
                              <Badge variant={order.priority === 'Urgent' ? 'destructive' : 'secondary'} className="text-xs">
                                {order.priority}
                              </Badge>
                            </div>
                            <p className="font-medium text-sm truncate">{order.client_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{order.service_name}</p>
                            <p className="text-xs font-mono mt-2">{formatCurrency(order.grand_total)}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {viewMode === 'list' && (
            <div className="mt-4 text-sm text-muted-foreground">
              Showing {filteredOrders.length} of {allOrders.length} orders
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete order <strong>{orderToDelete?.order_number}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOrderToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
